<template>
  <div id="charts">
    <v-overlay :value="loadingPatientData" absolute>
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <div v-if="!loadingPatientData">
      <div class="d-flex justify-space-between flex-md-row flex-column">
        <div class="small-card-wrapper">
          <v-card class="patient-info-card">
            <h1>{{ $t("patient_information") }}</h1>
            <div class="mb-2">
              <strong>{{ `${this.$t("initials")}: ` }}</strong>
              {{ `${this.patient.initials}` }}
            </div>
            <div class="mb-2">
              <strong>{{ `${this.$t("gender")}: ` }}</strong>
              {{ `${this.$t(this.patient.gender)}` }}
            </div>
            <div class="mb-2">
              <strong>{{ `${this.$t("age")}: ` }}</strong>
              {{ `${calculateAge(this.patient.birthdate)}` }}
            </div>
          </v-card>
        </div>
        <div class="easi-chart-wrapper mb-8">
          <bar-chart
            :chart-data="chartDataTotal"
            :options="chartOptionsTotal"
          />
        </div>
      </div>

      <v-card>
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="headNeckCharts = !headNeckCharts"
        >
          <h2>{{ $t("head_neck") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              headNeckCharts ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>

        <div v-show="headNeckCharts">
          <div class="mt-8 d-flex justify-space-between">
            <div class="body-part-title-wrapper">
              <img src="../../src/assets/head.png" />
            </div>
            <bar-chart
              class="range-chart"
              :chart-data="headNeckEczema"
              :options="chartOptionsBodyPart($t('eczema_range'))"
            />
          </div>
          <div class="d-flex justify-space-between flex-wrap">
            <bar-chart
              class="small-chart"
              :chart-data="headNeckErythema"
              :options="chartOptionsBodyPart($t('erytem'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="headNeckExcoriation"
              :options="chartOptionsBodyPart($t('excoriation'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="headNeckLichenification"
              :options="chartOptionsBodyPart($t('lichenification'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="headNeckPapula"
              :options="chartOptionsBodyPart($t('papules'))"
            />
          </div>
        </div>
      </v-card>

      <v-card class="mt-8">
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="upperLimbsCharts = !upperLimbsCharts"
        >
          <h2>{{ $t("upper_limbs") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              upperLimbsCharts ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>

        <div v-show="upperLimbsCharts">
          <div class="mt-8 d-flex justify-space-between">
            <div class="body-part-title-wrapper">
              <img src="../../src/assets/hands.png" />
            </div>
            <bar-chart
              class="range-chart"
              :chart-data="upperLimbsEczema"
              :options="chartOptionsBodyPart($t('eczema_range'))"
            />
          </div>
          <div class="d-flex flex-wrap">
            <bar-chart
              class="small-chart"
              :chart-data="upperLimbsErythema"
              :options="chartOptionsBodyPart($t('erytem'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="upperLimbsExcoriation"
              :options="chartOptionsBodyPart($t('excoriation'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="upperLimbsLichenification"
              :options="chartOptionsBodyPart($t('lichenification'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="upperLimbsPapula"
              :options="chartOptionsBodyPart($t('papules'))"
            />
          </div>
        </div>
      </v-card>

      <v-card class="mt-8">
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="torsoCharts = !torsoCharts"
        >
          <h2>{{ $t("torso") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              torsoCharts ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>

        <div v-show="torsoCharts">
          <div class="mt-8 d-flex justify-space-between">
            <div class="body-part-title-wrapper">
              <img src="../../src/assets/torso.png" />
            </div>
            <bar-chart
              class="range-chart"
              :chart-data="torsoEczema"
              :options="chartOptionsBodyPart($t('eczema_range'))"
            />
          </div>

          <div class="d-flex flex-wrap">
            <bar-chart
              class="small-chart"
              :chart-data="torsoErythema"
              :options="chartOptionsBodyPart($t('erytem'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="torsoExcoriation"
              :options="chartOptionsBodyPart($t('excoriation'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="torsoLichenification"
              :options="chartOptionsBodyPart($t('lichenification'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="torsoPapula"
              :options="chartOptionsBodyPart($t('papules'))"
            />
          </div>
        </div>
      </v-card>

      <v-card class="mt-8">
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="lowerLimbsCharts = !lowerLimbsCharts"
        >
          <h2>{{ $t("lower_limbs") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              lowerLimbsCharts ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>
        <div v-show="lowerLimbsCharts">
          <div class="mt-8 d-flex justify-space-between">
            <div class="body-part-title-wrapper">
              <img src="../../src/assets/back.png" />
            </div>
            <bar-chart
              class="range-chart"
              :chart-data="lowerLimbsEczema"
              :options="chartOptionsBodyPart($t('eczema_range'))"
            />
          </div>
          <div class="d-flex flex-wrap">
            <bar-chart
              class="small-chart"
              :chart-data="lowerLimbsErythema"
              :options="chartOptionsBodyPart($t('erytem'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="lowerLimbsExcoriation"
              :options="chartOptionsBodyPart($t('excoriation'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="lowerLimbsLichenification"
              :options="chartOptionsBodyPart($t('lichenification'))"
            />
            <bar-chart
              class="small-chart"
              :chart-data="lowerLimbsPapula"
              :options="chartOptionsBodyPart($t('papules'))"
            />
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import tokenMixin from "@/mixins/tokenMixin";
import BarChart from "@/components/BarChart.vue";

export default {
  name: "Plots",
  mixins: [tokenMixin],
  components: { BarChart },
  data() {
    return {
      patientForms: [],
      showHeadNeckCard: true,
      patient: null,
      multiplier: {
        head_neck: 0.1,
        upper_limbs: 0.2,
        torso: 0.3,
        lower_limbs: 0.4,
      },
      chartDataTotal: this.setBasicChartData(
        "Celokové skóre",
        "rgba(50, 76, 141, 0.5)",
        "rgba(50, 76, 141, 1)",
        true
      ),
      chartOptionsTotal: this.setChartOptions(
        72,
        4,
        "EASI skóre",
        "Datum",
        "EASI skóre"
      ),
      chartOptionsBodyPart: (title) =>
        this.setChartOptions(3, 0.5, "Závažnost", "Datum", title),
      loadingPatientData: true,
      headNeckData: [],
      upperLimbsData: [],
      torsoData: [],
      lowerLimbsData: [],
      headNeckCharts: true,
      upperLimbsCharts: true,
      torsoCharts: true,
      lowerLimbsCharts: true,
      headNeckEczema: this.setBasicChartData(
        this.$t("eczema_range"),
        "rgba(91,194,186, 0.5)",
        "rgba(91,194,186, 1)",
        false
      ),
      headNeckErythema: this.setBasicChartData(
        this.$t("erytem"),
        "rgba(91,194,186, 0.5)",
        "rgba(91,194,186, 1)",
        false
      ),
      headNeckExcoriation: this.setBasicChartData(
        this.$t("excoriation"),
        "rgba(91,194,186, 0.5)",
        "rgba(91,194,186, 1)",
        false
      ),
      headNeckLichenification: this.setBasicChartData(
        this.$t("lichenification"),
        "rgba(91,194,186, 0.5)",
        "rgba(91,194,186, 1)",
        false
      ),
      headNeckPapula: this.setBasicChartData(
        this.$t("papules"),
        "rgba(91,194,186, 0.5)",
        "rgba(91,194,186, 1)",
        false
      ),
      upperLimbsEczema: this.setBasicChartData(
        this.$t("eczema_range"),
        "rgb(255,212,0, 0.5)",
        "rgb(255,212,0, 1)",
        false
      ),
      upperLimbsErythema: this.setBasicChartData(
        this.$t("erytem"),
        "rgb(255,212,0, 0.5)",
        "rgb(255,212,0, 1)",
        false
      ),
      upperLimbsExcoriation: this.setBasicChartData(
        this.$t("excoriation"),
        "rgb(255,212,0, 0.5)",
        "rgb(255,212,0, 1)",
        false
      ),
      upperLimbsLichenification: this.setBasicChartData(
        this.$t("lichenification"),
        "rgb(255,212,0, 0.5)",
        "rgb(255,212,0, 1)",
        false
      ),
      upperLimbsPapula: this.setBasicChartData(
        this.$t("papules"),
        "rgb(255,212,0, 0.5)",
        "rgb(255,212,0, 1)",
        false
      ),
      torsoEczema: this.setBasicChartData(
        this.$t("eczema_range"),
        "rgba(254,111,188, 0.5)",
        "rgba(254,111,188)",
        false
      ),
      torsoErythema: this.setBasicChartData(
        this.$t("erytem"),
        "rgba(254,111,188, 0.5)",
        "rgba(254,111,188)",
        false
      ),
      torsoExcoriation: this.setBasicChartData(
        this.$t("excoriation"),
        "rgba(254,111,188, 0.5)",
        "rgba(254,111,188)",
        false
      ),
      torsoLichenification: this.setBasicChartData(
        this.$t("lichenification"),
        "rgba(254,111,188, 0.5)",
        "rgba(254,111,188)",
        false
      ),
      torsoPapula: this.setBasicChartData(
        this.$t("papules"),
        "rgba(254,111,188, 0.5)",
        "rgba(254,111,188)",
        false
      ),
      lowerLimbsEczema: this.setBasicChartData(
        this.$t("eczema_range"),
        "rgba(242,101,34, 0.5)",
        "rgba(242,101,34, 1)",
        false
      ),
      lowerLimbsErythema: this.setBasicChartData(
        this.$t("erytem"),
        "rgba(242,101,34, 0.5)",
        "rgba(242,101,34, 1)",
        false
      ),
      lowerLimbsExcoriation: this.setBasicChartData(
        this.$t("excoriation"),
        "rgba(242,101,34, 0.5)",
        "rgba(242,101,34, 1)",
        false
      ),
      lowerLimbsLichenification: this.setBasicChartData(
        this.$t("lichenification"),
        "rgba(242,101,34, 0.5)",
        "rgba(242,101,34, 1)",
        false
      ),
      lowerLimbsPapula: this.setBasicChartData(
        this.$t("papules"),
        "rgba(242,101,34, 0.5)",
        "rgba(242,101,34, 1)",
        false
      ),
    };
  },
  computed: {
    patientId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.fetchPatientDetail();
  },
  methods: {
    async fetchPatientDetail() {
      await this.$_checkTokenExpiration();
      ({
        body: { forms: this.patientForms, patient: this.patient },
      } = await this.$http.get(`patients/${this.patientId}`));
      if (this.patientForms.length > 0) {
        this.patientForms.reverse();
        this.patientForms.forEach((form) => {
          const bodyPartScores = [];
          form.body_parts.forEach((bodyPart) => {
            const bodyPartScore =
              (bodyPart.erythema +
                bodyPart.excoriation +
                bodyPart.lichenification +
                bodyPart.papula_plaque) *
              bodyPart.eczema_extent *
              this.multiplier[bodyPart.type];
            bodyPartScores.push({
              type: bodyPart.type,
              scores: bodyPartScore,
            });

            const bodyPartClone = JSON.parse(JSON.stringify(bodyPart));
            delete bodyPartClone.type;

            switch (bodyPart.type) {
              case "head_neck":
                this.headNeckData.push({
                  data: { ...bodyPartClone },
                  evaluatedOn: moment(form.evaluated_on).format("DD. MM. YYYY"),
                });
                break;
              case "upper_limbs":
                this.upperLimbsData.push({
                  data: { ...bodyPartClone },
                  evaluatedOn: moment(form.evaluated_on).format("DD. MM. YYYY"),
                });
                break;
              case "torso":
                this.torsoData.push({
                  data: { ...bodyPartClone },
                  evaluatedOn: moment(form.evaluated_on).format("DD. MM. YYYY"),
                });
                break;
              case "lower_limbs":
                this.lowerLimbsData.push({
                  data: { ...bodyPartClone },
                  evaluatedOn: moment(form.evaluated_on).format("DD. MM. YYYY"),
                });
                break;
            }
          });
          let initialValue = 0;
          this.chartDataTotal.datasets[0].data.push(
            Math.round(
              bodyPartScores.reduce(
                (accumulator, currentValue) =>
                  accumulator + currentValue.scores,
                initialValue
              ) * 10
            ) / 10
          );
          this.chartDataTotal.labels.push(
            moment(form.evaluated_on).format("DD. MM. YYYY")
          );
        });
        this.generatePlots();
      }
      this.loadingPatientData = false;
    },
    generatePlots() {
      // head neck plots
      this.headNeckData.forEach((headNeck) => {
        // eczema
        this.headNeckEczema.datasets[0].data.push(headNeck.data.eczema_extent);
        this.headNeckEczema.labels.push(headNeck.evaluatedOn);
        // erythema
        this.headNeckErythema.datasets[0].data.push(headNeck.data.erythema);
        this.headNeckErythema.labels.push(headNeck.evaluatedOn);
        // excoriation
        this.headNeckExcoriation.datasets[0].data.push(
          headNeck.data.excoriation
        );
        this.headNeckExcoriation.labels.push(headNeck.evaluatedOn);
        // lichenification
        this.headNeckLichenification.datasets[0].data.push(
          headNeck.data.lichenification
        );
        this.headNeckLichenification.labels.push(headNeck.evaluatedOn);
        // papula
        this.headNeckPapula.datasets[0].data.push(headNeck.data.papula_plaque);
        this.headNeckPapula.labels.push(headNeck.evaluatedOn);
      });

      // upper limbs plots
      this.upperLimbsData.forEach((upperLimbs) => {
        // eczema
        this.upperLimbsEczema.datasets[0].data.push(
          upperLimbs.data.eczema_extent
        );
        this.upperLimbsEczema.labels.push(upperLimbs.evaluatedOn);
        // erythema
        this.upperLimbsErythema.datasets[0].data.push(upperLimbs.data.erythema);
        this.upperLimbsErythema.labels.push(upperLimbs.evaluatedOn);
        // excoriation
        this.upperLimbsExcoriation.datasets[0].data.push(
          upperLimbs.data.excoriation
        );
        this.upperLimbsExcoriation.labels.push(upperLimbs.evaluatedOn);
        // lichenification
        this.upperLimbsLichenification.datasets[0].data.push(
          upperLimbs.data.lichenification
        );
        this.upperLimbsLichenification.labels.push(upperLimbs.evaluatedOn);
        // papula
        this.upperLimbsPapula.datasets[0].data.push(
          upperLimbs.data.papula_plaque
        );
        this.upperLimbsPapula.labels.push(upperLimbs.evaluatedOn);
      });

      // torso plots
      this.torsoData.forEach((torso) => {
        // eczema
        this.torsoEczema.datasets[0].data.push(torso.data.eczema_extent);
        this.torsoEczema.labels.push(torso.evaluatedOn);
        // erythema
        this.torsoErythema.datasets[0].data.push(torso.data.erythema);
        this.torsoErythema.labels.push(torso.evaluatedOn);
        // excoriation
        this.torsoExcoriation.datasets[0].data.push(torso.data.excoriation);
        this.torsoExcoriation.labels.push(torso.evaluatedOn);
        // lichenification
        this.torsoLichenification.datasets[0].data.push(
          torso.data.lichenification
        );
        this.torsoLichenification.labels.push(torso.evaluatedOn);
        // papula
        this.torsoPapula.datasets[0].data.push(torso.data.papula_plaque);
        this.torsoPapula.labels.push(torso.evaluatedOn);
      });

      // lower limbs plots
      this.lowerLimbsData.forEach((lowerLimbs) => {
        // eczema
        this.lowerLimbsEczema.datasets[0].data.push(
          lowerLimbs.data.eczema_extent
        );
        this.lowerLimbsEczema.labels.push(lowerLimbs.evaluatedOn);
        // erythema
        this.lowerLimbsErythema.datasets[0].data.push(lowerLimbs.data.erythema);
        this.lowerLimbsErythema.labels.push(lowerLimbs.evaluatedOn);
        // excoriation
        this.lowerLimbsExcoriation.datasets[0].data.push(
          lowerLimbs.data.excoriation
        );
        this.lowerLimbsExcoriation.labels.push(lowerLimbs.evaluatedOn);
        // lichenification
        this.lowerLimbsLichenification.datasets[0].data.push(
          lowerLimbs.data.lichenification
        );
        this.lowerLimbsLichenification.labels.push(lowerLimbs.evaluatedOn);
        // papula
        this.lowerLimbsPapula.datasets[0].data.push(
          lowerLimbs.data.papula_plaque
        );
        this.lowerLimbsPapula.labels.push(lowerLimbs.evaluatedOn);
      });
    },
    setBasicChartData(label, backgroundColor, borderColor, showEasiIndex) {
      return {
        datasets: [
          {
            backgroundColor: backgroundColor,
            borderColor: borderColor,
            borderWidth: 1,
            data: [],
            label: label,
            datalabels: {
              color: "gray",
              anchor: "end",
              align: "top",
              formatter: function(value, context) {
                if (showEasiIndex) {
                  let firstVisitScore = context.chart.data.datasets[0].data[0];
                  return `EASI-${(
                    ((firstVisitScore - value) / firstVisitScore) *
                    100
                  ).toFixed(0)}`;
                }
              },
            },
          },
        ],
        labels: [],
      };
    },
    setChartOptions(suggestedMax, stepSize, yLabel, xLabel, title) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                suggestedMax: suggestedMax,
                stepSize: stepSize,
              },
              scaleLabel: {
                display: true,
                labelString: yLabel,
              },
            },
          ],
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: xLabel,
              },
            },
          ],
        },
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: title,
          padding: 35,
        },
      };
    },
    calculateAge(birthDate) {
      const momentBirthDate = moment(birthDate);
      return moment.duration(moment().diff(momentBirthDate)).years();
    },
  },
};
</script>

<style lang="scss" scoped>
#charts {
  padding: 2rem 5rem;

  .v-card {
    box-shadow: 0px 0px 18px 0px rgba(209, 209, 209, 1);
    padding: 1rem;
  }

  .small-card-wrapper {
    width: 20%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 2rem;
  }

  .patient-info-card {
    padding: 1rem;
    font-size: 1.5rem;
    height: 60%;

    h1 {
      margin-bottom: 1rem;
      font-size: 1.7rem;
    }

    strong {
      color: #324c8d;
    }
  }

  .partner-banner {
    height: 35%;
  }

  .easi-chart-wrapper {
    width: 78%;
    box-shadow: 0px 0px 18px 0px rgba(209, 209, 209, 1);
    padding: 1rem;
  }

  .body-part-title-wrapper {
    padding: 0 2rem;
    width: 39%;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      max-width: 500px;
      width: 100%;
    }
  }

  .range-chart {
    width: 60%;
  }

  .small-chart {
    width: 24%;
    margin-top: 2rem;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  @media screen and (max-width: 1100px) {
    padding: 2rem;

    .body-part-title-wrapper {
      display: none;
    }

    .range-chart {
      width: 100%;
    }
  }

  @media screen and (max-width: 959px) {
    padding: 1rem;

    .small-card-wrapper,
    .easi-chart-wrapper,
    .small-chart {
      width: 100%;
    }

    .patient-info-card {
      margin-bottom: 2rem;
    }
  }
}
</style>
